import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, FileText, Zap, RefreshCw, Code2, Upload, Clock,
  Shield, Users, CreditCard, Palette, Menu, X, Clock3,
  Calendar, ChevronRight, ChevronDown, ExternalLink,
  MessageCircle, Home, BookOpen,
} from 'lucide-react';
import { CATEGORIES, getCategoryById, getArticleById } from '../help/helpCenterData.js';
import { DocRenderer } from '../components/HelpCenter/DocRenderer.jsx';
import '../styles/docs.css';

const ICON_MAP = {
  'file-text': <FileText size={18} strokeWidth={1.5} />,
  zap: <Zap size={18} strokeWidth={1.5} />,
  'refresh-cw': <RefreshCw size={18} strokeWidth={1.5} />,
  'code-2': <Code2 size={18} strokeWidth={1.5} />,
  upload: <Upload size={18} strokeWidth={1.5} />,
  clock: <Clock size={18} strokeWidth={1.5} />,
  shield: <Shield size={18} strokeWidth={1.5} />,
  users: <Users size={18} strokeWidth={1.5} />,
  'credit-card': <CreditCard size={18} strokeWidth={1.5} />,
  palette: <Palette size={18} strokeWidth={1.5} />,
};

function SidebarItem({ article, isActive, categoryId, color }) {
  return (
    <Link to={`/c/${categoryId}/${article.id}`} style={{ textDecoration: 'none', display: 'block' }}>
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          padding: '0.5rem 1rem 0.5rem 1.25rem',
          borderRadius: 8,
          marginBottom: 2,
          background: isActive ? `${color}12` : 'transparent',
          color: isActive ? '#F8FAFC' : '#94A3B8',
          fontSize: '0.875rem',
          fontWeight: isActive ? 500 : 400,
          transition: 'all 0.15s',
          borderLeft: isActive ? `2px solid ${color}` : '2px solid transparent',
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            e.currentTarget.style.color = '#F8FAFC';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#94A3B8';
          }
        }}
      >
        <BookOpen size={13} style={{ flexShrink: 0, opacity: isActive ? 1 : 0.6 }} />
        <span style={{ lineHeight: 1.45 }}>{article.title}</span>
      </div>
    </Link>
  );
}

function CategorySection({ cat, activeCatId, activeArtId }) {
  const isActive = cat.id === activeCatId;
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  return (
    <div style={{ marginBottom: '0.25rem' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          padding: '0.5rem 0.75rem',
          background: 'transparent',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          color: isActive ? '#F8FAFC' : '#64748B',
          fontWeight: isActive ? 600 : 500,
          fontSize: '0.8125rem',
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          transition: 'all 0.15s',
          textAlign: 'left',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#94A3B8';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = isActive ? '#F8FAFC' : '#64748B';
        }}
      >
        <span style={{ color: cat.color, display: 'flex', alignItems: 'center' }}>{ICON_MAP[cat.icon]}</span>
        <span style={{ flex: 1 }}>{cat.title}</span>
        <ChevronDown size={13} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: 'hidden' }}>
            <div style={{ paddingLeft: '0.5rem' }}>
              {cat.articles.map((art) => (
                <SidebarItem key={art.id} article={art} isActive={art.id === activeArtId && cat.id === activeCatId} categoryId={cat.id} color={cat.color} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TableOfContents({ content }) {
  const headings = [];
  const h2Matches = [...String(content || '').matchAll(/^## (.+)$/gm)];
  const h3Matches = [...String(content || '').matchAll(/^### (.+)$/gm)];

  h2Matches.forEach((m) => headings.push({ level: 2, text: m[1] }));
  h3Matches.forEach((m) => headings.push({ level: 3, text: m[1] }));

  if (!headings.length) return null;

  return (
    <div className="hc-toc" style={{ padding: '0 1rem', borderLeft: '1px solid rgba(51,65,85,0.5)' }}>
      <p style={{ color: '#475569', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>En esta página</p>
      {headings.map((h, i) => (
        <span
          key={i}
          style={{
            display: 'block',
            color: '#64748B',
            fontSize: h.level === 2 ? '0.8125rem' : '0.775rem',
            paddingLeft: h.level === 3 ? '0.75rem' : 0,
            paddingBottom: '0.5rem',
            lineHeight: 1.4,
            borderLeft: h.level === 3 ? '1px solid rgba(51,65,85,0.5)' : 'none',
          }}
        >
          {h.text}
        </span>
      ))}
    </div>
  );
}

export default function CategoryPage() {
  const { categoryId, articleId } = useParams();
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [helpfulVote, setHelpfulVote] = useState(null);

  const category = getCategoryById(categoryId || '');
  const currentArticle = articleId ? getArticleById(categoryId || '', articleId) : category?.articles?.[0];

  useEffect(() => {
    if (categoryId && !category) navigate('/documentacion');
  }, [category, categoryId, navigate]);

  useEffect(() => {
    if (category && !articleId) {
      navigate(`/c/${category.id}/${category.articles[0].id}`, { replace: true });
    }
  }, [category, articleId, navigate]);

  if (!category || !currentArticle) {
    return (
      <div style={{ minHeight: '100vh', background: '#0F172A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
        Cargando...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0F172A', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(15,23,42,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(51,65,85,0.6)',
          flexShrink: 0,
        }}
      >
        <div style={{ maxWidth: '100%', padding: '0 1.5rem', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
            <button
              type="button"
              className="hc-sidebar-mobile-toggle"
              onClick={() => setMobileSidebarOpen(true)}
              style={{
                background: 'none',
                border: '1px solid rgba(51,65,85,0.6)',
                borderRadius: 8,
                padding: '5px 8px',
                color: '#94A3B8',
                cursor: 'pointer',
                display: 'none',
                alignItems: 'center',
              }}
            >
              <Menu size={16} />
            </button>

            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                textDecoration: 'none',
                color: '#94A3B8',
                fontSize: '0.875rem',
                transition: 'color 0.15s',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#F8FAFC')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#94A3B8')}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  background: 'linear-gradient(135deg, #4F46E5, #22D3EE)',
                  borderRadius: 7,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FileText size={12} color="white" />
              </div>
              <span style={{ fontWeight: 600, color: '#F8FAFC', fontSize: '0.875rem' }}>ReportEngine</span>
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#475569', fontSize: '0.8125rem', overflow: 'hidden' }}>
              <ChevronRight size={13} style={{ flexShrink: 0 }} />
              <Link to="/documentacion" style={{ color: '#64748B', textDecoration: 'none', transition: 'color 0.15s', flexShrink: 0 }}>
                Docs
              </Link>
              <ChevronRight size={13} style={{ flexShrink: 0 }} />
              <span style={{ color: '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{category.title}</span>
              <ChevronRight size={13} style={{ flexShrink: 0, color: '#334155' }} />
              <span style={{ color: '#F8FAFC', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{currentArticle.title}</span>
            </div>
          </div>

          <nav style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
            <Link to="/documentacion" style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748B', fontSize: '0.8125rem', textDecoration: 'none', transition: 'color 0.15s' }}>
              <Home size={13} />
              Help Center
            </Link>
            <Link to="/referencia-api" style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748B', fontSize: '0.8125rem', textDecoration: 'none', transition: 'color 0.15s' }}>
              API Reference
              <ExternalLink size={11} />
            </Link>
          </nav>
        </div>
      </header>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <aside
          className="hc-sidebar-desktop"
          style={{
            width: 260,
            flexShrink: 0,
            borderRight: '1px solid rgba(51,65,85,0.5)',
            background: '#0F172A',
            height: 'calc(100vh - 60px)',
            position: 'sticky',
            top: 60,
            overflowY: 'auto',
            padding: '1.5rem 0.75rem',
          }}
        >
          <Link
            to="/documentacion"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#64748B',
              fontSize: '0.8125rem',
              textDecoration: 'none',
              padding: '0.375rem 0.75rem',
              borderRadius: 8,
              marginBottom: '1.25rem',
              transition: 'all 0.15s',
            }}
          >
            <ArrowLeft size={14} />
            Volver al Help Center
          </Link>

          <div style={{ height: 1, background: 'rgba(51,65,85,0.5)', margin: '0 0.5rem 1.25rem' }} />

          {CATEGORIES.map((cat) => (
            <CategorySection key={cat.id} cat={cat} activeCatId={category.id} activeArtId={currentArticle.id} />
          ))}
        </aside>

        <AnimatePresence>
          {mobileSidebarOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileSidebarOpen(false)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 60 }}
              />
              <motion.div
                className="hc-drawer-open"
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ duration: 0.25 }}
                style={{
                  position: 'fixed',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 280,
                  background: '#0F172A',
                  borderRight: '1px solid rgba(51,65,85,0.6)',
                  zIndex: 70,
                  overflowY: 'auto',
                  padding: '1rem 0.75rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', padding: '0 0.5rem' }}>
                  <span style={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.875rem' }}>Documentación</span>
                  <button type="button" onClick={() => setMobileSidebarOpen(false)} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', padding: 4 }}>
                    <X size={16} />
                  </button>
                </div>
                {CATEGORIES.map((cat) => (
                  <CategorySection key={cat.id} cat={cat} activeCatId={category.id} activeArtId={currentArticle.id} />
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <main style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', padding: '2.5rem 1.5rem' }}>
          <div style={{ width: '100%', maxWidth: 760 }}>
            <AnimatePresence mode="wait">
              <motion.article key={currentArticle.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                <div style={{ marginBottom: '2.5rem' }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: `${category.color}15`,
                      border: `1px solid ${category.color}30`,
                      borderRadius: 99,
                      padding: '3px 12px',
                      marginBottom: '1rem',
                    }}
                  >
                    <span style={{ color: category.color, display: 'flex', alignItems: 'center' }}>{ICON_MAP[category.icon]}</span>
                    <span style={{ color: category.color, fontSize: '0.75rem', fontWeight: 500 }}>{category.title}</span>
                  </div>

                  <h1 style={{ color: '#F8FAFC', fontSize: 'clamp(1.5rem, 3vw, 1.875rem)', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.25, marginBottom: '1rem' }}>
                    {currentArticle.title}
                  </h1>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748B', fontSize: '0.8125rem' }}>
                      <Calendar size={13} />
                      Actualizado {currentArticle.updatedAt}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748B', fontSize: '0.8125rem' }}>
                      <Clock3 size={13} />
                      {currentArticle.readTime} de lectura
                    </span>
                  </div>

                  <div style={{ height: 1, background: 'rgba(51,65,85,0.5)', marginTop: '1.5rem' }} />
                </div>

                <DocRenderer content={currentArticle.content} />

                <div
                  style={{
                    marginTop: '3rem',
                    paddingTop: '2rem',
                    borderTop: '1px solid rgba(51,65,85,0.5)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    flexWrap: 'wrap',
                  }}
                >
                  {(() => {
                    const idx = category.articles.findIndex((a) => a.id === currentArticle.id);
                    const prev = idx > 0 ? category.articles[idx - 1] : null;
                    return prev ? (
                      <Link to={`/c/${category.id}/${prev.id}`} style={{ textDecoration: 'none' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.625rem',
                            padding: '0.75rem 1rem',
                            background: '#1E293B',
                            border: '1px solid rgba(51,65,85,0.6)',
                            borderRadius: 10,
                            color: '#94A3B8',
                            fontSize: '0.875rem',
                            transition: 'all 0.15s',
                            maxWidth: 260,
                          }}
                        >
                          <ArrowLeft size={14} style={{ flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: '0.7rem', color: '#475569', marginBottom: 2 }}>Anterior</div>
                            <div style={{ fontWeight: 500 }}>{prev.title}</div>
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <div />
                    );
                  })()}

                  {(() => {
                    const idx = category.articles.findIndex((a) => a.id === currentArticle.id);
                    const next = idx < category.articles.length - 1 ? category.articles[idx + 1] : null;
                    return next ? (
                      <Link to={`/c/${category.id}/${next.id}`} style={{ textDecoration: 'none' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.625rem',
                            padding: '0.75rem 1rem',
                            background: '#1E293B',
                            border: '1px solid rgba(51,65,85,0.6)',
                            borderRadius: 10,
                            color: '#94A3B8',
                            fontSize: '0.875rem',
                            transition: 'all 0.15s',
                            maxWidth: 260,
                            textAlign: 'right',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '0.7rem', color: '#475569', marginBottom: 2 }}>Siguiente</div>
                            <div style={{ fontWeight: 500 }}>{next.title}</div>
                          </div>
                          <ChevronRight size={14} style={{ flexShrink: 0 }} />
                        </div>
                      </Link>
                    ) : null;
                  })()}
                </div>

                <div
                  style={{
                    marginTop: '2.5rem',
                    padding: '1.5rem',
                    background: 'rgba(30,41,59,0.5)',
                    border: '1px solid rgba(51,65,85,0.5)',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <p style={{ color: '#94A3B8', fontSize: '0.875rem', margin: 0 }}>¿Esta página fue útil?</p>
                    <p style={{ color: '#64748B', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>¿No encontraste lo que buscabas?</p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.625rem' }}>
                    {[
                      { label: '👍 Sí', value: 'yes' },
                      { label: '👎 No', value: 'no' },
                    ].map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => setHelpfulVote(item.value)}
                        style={{
                          padding: '0.5rem 0.875rem',
                          background: helpfulVote === item.value ? 'rgba(79,70,229,0.16)' : 'transparent',
                          border: helpfulVote === item.value ? '1px solid rgba(79,70,229,0.6)' : '1px solid rgba(51,65,85,0.7)',
                          borderRadius: 8,
                          color: helpfulVote === item.value ? '#c7d2fe' : '#64748B',
                          fontSize: '0.8125rem',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                    <a
                      href="mailto:soporte@reportengine.app"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '0.5rem 0.875rem',
                        background: '#4F46E5',
                        borderRadius: 8,
                        color: 'white',
                        fontSize: '0.8125rem',
                        textDecoration: 'none',
                        transition: 'background 0.15s',
                        border: '1px solid transparent',
                      }}
                    >
                      <MessageCircle size={13} />
                      Soporte
                    </a>
                  </div>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>
        </main>

        <div className="hc-sidebar-desktop" style={{ width: 220, flexShrink: 0, padding: '2.5rem 0', height: 'calc(100vh - 60px)', position: 'sticky', top: 60 }}>
          <TableOfContents content={currentArticle.content} />
        </div>
      </div>

      <footer style={{ background: '#0F172A', borderTop: '1px solid rgba(51,65,85,0.4)', padding: '1.25rem 1.5rem' }}>
        <div style={{ maxWidth: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <span style={{ color: '#334155', fontSize: '0.8125rem' }}>© 2026 ReportEngine</span>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {[
              { label: 'API Reference', href: '/referencia-api', external: false },
              { label: 'Estado del sistema', href: '/estado-del-sistema', external: false },
              { label: 'Términos', href: '/terminos-de-servicio', external: false },
              { label: 'Privacidad', href: '/politica-de-privacidad', external: false },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noreferrer' : undefined}
                style={{ color: '#475569', fontSize: '0.8125rem', textDecoration: 'none', transition: 'color 0.15s' }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
